import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog, confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { subDays } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import type { Person } from '../../users/entities/person.js';
import { UserEmailsAPI } from '../UserEmailsAPI.js';
import { EmailChangeTracker } from './EmailChangeTracker.js';

/**
 * TODO: whether the user overrides the email or starts the change process only depends on whether the
 * user is himself (he himself can only ever start the process). Move this to two abac permissions.
 */

/** One step of the three-step progress row. */
const ProgressStep = (props: { done: boolean; tooltip: string }) => html`
  <div class="tooltip" data-tip=${props.tooltip} ${hoverTooltip()}>
    <span
      class="${
        props.done
          ? 'icon-[material-symbols--check-circle] text-success'
          : 'icon-[material-symbols--play-circle] text-neutral'
      } text-[4rem]"
    ></span>
  </div>
`;

const ChangeEmailProcessContent = stateful(
  (props: { user: EntityData<Person>; userIsCurrentUser: boolean; resolve: () => void; reject: () => void }) => {
    const [email, setEmail] = useState('');

    const { value: processTracker } = useQuery(
      () =>
        props.user._id
          ? UserEmailsAPI.subscribeEmailChange(props.user._id)(selectionSet(() => EmailChangeTracker, 1))
          : of(null),
      [props.user._id],
    );

    const startedProcess = processTracker?.startedProcess;
    const showProgress = !!startedProcess && startedProcess >= subDays(new Date(), 2);

    const possessive = props.userIsCurrentUser ? 'Ihre' : 'Die';

    const handleEmailChange = async () => {
      if (!email) return alertDialog('Bitte geben Sie eine E-Mail Adresse an.');
      if (!emailRegexCheck(email)) return alertDialog('Die E-Mail Adresse ist ungueltig.');
      // same email gets rejected
      if (props.user.email === email) return alertDialog('Bitte geben Sie eine andere E-Mail Adresse an.');

      // if the user is myself i want to start the self email change process
      if (props.userIsCurrentUser) {
        await guardDialog(() => UserEmailsAPI.startEmailChange(props.user._id, email)(), {
          success: () => {
            void alertDialog(
              'Wir haben eine E-Mail zu Ihrer aktuellen Adresse gesendet. Bitte folgen Sie den Anweisungen dort.',
            );
          },
        });
        return;
      }

      // if the user is an admin and wants to change someone elses mail i just save the new mail and set email confirmed to false
      await guardDialog(async () => {
        props.user.email = email;
        props.user.emailConfirmed = false;
        await constructValue<Person>(props.user).save();

        if (!(await confirmDialog('Der neuen E-Mail-Adresse direkt eine Verifizierungsmail senden?'))) return;

        await UserEmailsAPI.sendVerificationEmail(props.user._id)();
      });
      props.resolve();
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'E-Mail Adressänderung', close: () => props.reject(), closeLabel: 'Dialog schließen' })}
        ${
          showProgress
            ? html`
                <div class="flex flex-row items-center justify-center">
                  ${ProgressStep({ done: true, tooltip: 'Die E-Mail-Adressänderung wurde gestartet' })}
                  <div class="bg-neutral h-0.5 w-8"></div>
                  ${ProgressStep({
                    done: !!processTracker?.confirmedOldEmail,
                    tooltip: processTracker?.confirmedOldEmail
                      ? `${possessive} vorherige E-Mail-Adresse wurde erfolgreich verifiziert`
                      : `${possessive} vorherige E-Mail-Adresse wurde noch nicht verifiziert.`,
                  })}
                  <div class="bg-neutral h-0.5 w-8"></div>
                  ${ProgressStep({
                    done: !!processTracker?.confirmedNewEmail,
                    tooltip: processTracker?.confirmedNewEmail
                      ? `${possessive} neue E-Mail wurde erfolgreich verifiziert.`
                      : `${possessive} neue E-Mail wurde nocht nicht verifiziert.`,
                  })}
                </div>
              `
            : nothing
        }
        ${
          props.userIsCurrentUser && showProgress
            ? nothing
            : html`
                <p class="max-w-[600px]">
                  ${
                    props.userIsCurrentUser
                      ? 'Hier können Sie den Prozess zur E-Mail-Adressänderung starten. Um zu beginnen, geben Sie bitte eine neue E-Mail Adresse ein und folgen den weiteren Anweisungen.'
                      : showProgress
                        ? 'Der User hat den Prozess bereits gestartet. Oben können Sie den Fortschritt verfolgen. Wenn es hier zu einem Fehler gekommen sein sollte können Sie den Prozess mit der Eingabe der neuen E-Mail Adresse überspringen.'
                        : 'Hier können Sie dem Nutzer eine neue E-Mail-Adresse vergeben. Dies sollte der Nutzer im Normalfall selbst übernehmen. Dies nur eine Absicherung.'
                  }
                </p>
                ${TextInput({
                  value: email,
                  type: 'email',
                  placeholder: 'Neue E-Mail Adresse',
                  placeholderMode: 'hide',
                  onChanged: value => setEmail(value ?? ''),
                  onPicked: value => setEmail(value ?? ''),
                  onSubmit: () => void handleEmailChange(),
                })}
                <div class="flex justify-end">
                  <button class="btn btn-primary" @click=${handleEmailChange}>E-Mail-Adresse ändern</button>
                </div>
              `
        }
      </div>
    `;
  },
);

/** Opens the e-mail change dialog for `user`. Resolves once the dialog is gone. */
export const showChangeEmailProcessDialog = async (props: {
  user: EntityData<Person>;
  userIsCurrentUser: boolean;
}): Promise<void> => {
  try {
    await showDialog<void>({
      content: (resolve, reject) => ChangeEmailProcessContent({ ...props, resolve, reject }),
      modal: true,
      modalBoxClasses: 'max-w-2xl',
    });
  } catch {
    // dialog was dismissed
  }
};
